import { useState } from "react";

const SUBMIT_URL = "https://readdy.ai/api/form/da21eirk8r5te5mqsdm0";

const subjectOptions = ["追更建议", "作品投稿", "版权合作", "功能反馈", "其他咨询"];

export default function ContactForm() {
  const [status, setStatus] = useState<"idle" | "loading" | "success" | "error">("idle");
  const [message, setMessage] = useState("");

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const data = new FormData(form);

    // 蜜罐检测：若被机器人填了隐藏字段，则静默当作成功，不真正提交
    const honeypot = (data.get("website_alt") as string)?.trim() ?? "";
    if (honeypot) {
      setStatus("success");
      setMessage("感谢你的来信，我们会在 1-2 个工作日内回复你。");
      return;
    }

    const payload = new URLSearchParams();
    for (const [key, value] of data.entries()) {
      if (key === "website_alt") continue;
      payload.append(key, String(value));
    }

    setStatus("loading");
    setMessage("");
    try {
      const res = await fetch(SUBMIT_URL, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: payload.toString(),
      });
      const responseText = await res.text();
      let parsed: Record<string, unknown> | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }
      const metaMessage = (parsed as any)?.meta?.message ?? (parsed as any)?.message ?? "";
      const serverText = responseText.toLowerCase();
      const ok = res.ok && parsed?.code === "OK" && !serverText.includes("spam");

      if (!ok) {
        setStatus("error");
        setMessage(metaMessage || "提交失败，请稍后再试。");
        return;
      }
      setStatus("success");
      setMessage("感谢你的来信，我们会在 1-2 个工作日内回复你。");
      form.reset();
    } catch {
      setStatus("error");
      setMessage("网络异常，提交失败，请稍后再试。");
    }
  };

  return (
    <form
      onSubmit={handleSubmit}
      data-readdy-form="contact-tiantian"
      className="rounded-3xl bg-background-50 border border-background-200 p-5 md:p-8"
    >
      <h3 className="font-heading text-xl text-foreground-950">给我们留言</h3>
      <p className="mt-1 text-sm text-foreground-500">关于追更、投稿、版权或功能的任何想法，都欢迎告诉我们。</p>

      <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label htmlFor="name" className="block text-sm font-medium text-foreground-700 mb-1.5">
            你的称呼
          </label>
          <input
            id="name"
            name="name"
            type="text"
            required
            placeholder="怎么称呼你"
            className="w-full h-11 rounded-xl bg-background-100 border border-background-200 px-4 text-sm text-foreground-900 outline-none focus:border-primary-300 focus:ring-2 focus:ring-primary-200/60 placeholder:text-foreground-400"
          />
        </div>
        <div>
          <label htmlFor="email" className="block text-sm font-medium text-foreground-700 mb-1.5">
            邮箱
          </label>
          <input
            id="email"
            name="email"
            type="email"
            required
            placeholder="用于接收回复"
            className="w-full h-11 rounded-xl bg-background-100 border border-background-200 px-4 text-sm text-foreground-900 outline-none focus:border-primary-300 focus:ring-2 focus:ring-primary-200/60 placeholder:text-foreground-400"
          />
        </div>
      </div>

      <div className="mt-4">
        <label htmlFor="subject" className="block text-sm font-medium text-foreground-700 mb-1.5">
          咨询类型
        </label>
        <select
          id="subject"
          name="subject"
          className="w-full h-11 rounded-xl bg-background-100 border border-background-200 px-4 text-sm text-foreground-900 outline-none focus:border-primary-300 focus:ring-2 focus:ring-primary-200/60"
        >
          {subjectOptions.map((o) => (
            <option key={o} value={o}>
              {o}
            </option>
          ))}
        </select>
      </div>

      <div className="mt-4">
        <label htmlFor="message" className="block text-sm font-medium text-foreground-700 mb-1.5">
          留言内容
        </label>
        <textarea
          id="message"
          name="message"
          required
          maxLength={500}
          rows={4}
          placeholder="请写下你的留言（500 字以内）"
          className="w-full rounded-xl bg-background-100 border border-background-200 px-4 py-3 text-sm text-foreground-900 outline-none focus:border-primary-300 focus:ring-2 focus:ring-primary-200/60 placeholder:text-foreground-400 resize-none"
        ></textarea>
        <p className="mt-1 text-right text-xs text-foreground-400">最多 500 字</p>
      </div>

      {/* 防垃圾字段 */}
      <input type="text" name="website_alt" tabIndex={-1} autoComplete="off" aria-hidden="true" readOnly />

      <div className="mt-5 flex flex-col sm:flex-row items-start sm:items-center gap-3">
        <button
          type="submit"
          disabled={status === "loading"}
          className="inline-flex items-center justify-center gap-1.5 h-12 px-7 rounded-full bg-primary-500 text-background-50 text-sm font-medium whitespace-nowrap cursor-pointer hover:bg-primary-600 disabled:opacity-60 transition-colors"
        >
          {status === "loading" ? "正在发送…" : "发送留言"}
          {status === "loading" ? null : <i className="ri-send-plane-line"></i>}
        </button>
      </div>

      {status === "success" && (
        <p className="mt-4 flex items-center gap-1.5 text-sm text-secondary-700 bg-secondary-100 rounded-xl px-4 py-3">
          <i className="ri-checkbox-circle-fill"></i>
          {message}
        </p>
      )}
      {status === "error" && (
        <p className="mt-4 flex items-center gap-1.5 text-sm text-primary-700 bg-primary-100 rounded-xl px-4 py-3">
          <i className="ri-error-warning-fill"></i>
          {message}
        </p>
      )}
    </form>
  );
}